/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { ShoppingAssistantProduct } from '../../shoppingAssistant';
import { ShoppingBagIcon, StarIcon, CheckCircleIcon } from '../icons';

import { useTranslation } from '../../i18n/useTranslation';

interface ProductCardProps {
    product: ShoppingAssistantProduct;
    onAddToBasket?: (product: any) => void; // Using any for now to match App's RetailItem or ShoppingAssistantProduct
}

export const ProductCard: React.FC<ProductCardProps> = ({ product, onAddToBasket }) => {
    const { t } = useTranslation();

    return (
        <div className="flex-shrink-0 w-48 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm hover:shadow-md transition-shadow group">
            <div className="relative h-48 overflow-hidden">
                <img
                    src={product.imageUrl}
                    alt={product.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                {product.isWardrobeItem && (
                    <div className="absolute inset-0 bg-indigo-600/10 backdrop-blur-[1px] flex items-center justify-center">
                        <span className="bg-indigo-600 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow-lg uppercase tracking-wider">
                            {t('shoppingAssistant_product_in_wardrobe')}
                        </span>
                    </div>
                )}
                {product.availability === 'Low Stock' && !product.isWardrobeItem && (
                    <span className="absolute top-2 left-2 bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">
                        {t('shoppingAssistant_product_low_stock')}
                    </span>
                )}
            </div>

            <div className="p-3">
                <div className="flex justify-between items-start mb-1">
                    <p className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">
                        {product.brand || 'Luxury'}
                    </p>
                    {product.rating && (
                        <div className="flex items-center gap-0.5">
                            <StarIcon className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                            <span className="text-[10px] font-medium text-gray-500">{product.rating}</span>
                        </div>
                    )}
                </div>

                <h4 className="text-xs font-bold text-gray-900 dark:text-white truncate mb-2" title={product.title}>
                    {product.title}
                </h4>

                <div className="flex items-center justify-between mt-auto">
                    <div className="flex flex-col">
                        <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">
                            {product.isWardrobeItem ? t('shoppingAssistant_product_price_wardrobe') : product.price}
                        </span>
                        {product.originalPrice && !product.isWardrobeItem && (
                            <span className="text-[10px] text-gray-400 line-through">
                                {product.originalPrice}
                            </span>
                        )}
                    </div>

                    <button
                        onClick={() => onAddToBasket?.(product)}
                        disabled={product.isWardrobeItem}
                        className={`p-2 rounded-lg transition-colors ${product.isWardrobeItem
                            ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 cursor-default'
                            : 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-600 hover:text-white'}`}
                        aria-label={product.isWardrobeItem ? t('shoppingAssistant_product_owned') : t('shoppingAssistant_product_add_to_basket')}
                        title={product.isWardrobeItem ? t('shoppingAssistant_product_already_in_wardrobe') : t('shoppingAssistant_product_add_to_basket')}
                    >
                        {product.isWardrobeItem ? <CheckCircleIcon className="w-4 h-4" /> : <ShoppingBagIcon className="w-4 h-4" />}
                    </button>
                </div>
            </div>
        </div>
    );
};
